import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import {
	MatLegacyTab as MatTab,
	MatLegacyTabGroup as MatTabGroup,
	MatLegacyTabHeader as MatTabHeader,
} from "@angular/material/legacy-tabs";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	BettingActiveLimit,
	BettingLimit,
	BettingLimitAction,
	BettingLimitConfig,
	BettingLimitContent,
	BettingLimitForm,
	BettingLimitPeriod,
	BettingLimitService,
	BettingLimitSubmission,
	BettingLimitType,
	LIMIT_PERIODS,
	LimitPeriod,
	MultiSessionLimitConfigService,
	ResponsibleGamingUiConfigService,
	TRANSLATION_PREFIX,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	AuthSelector,
	AuthService,
	CORE_ROUTE,
	CurrencyAction,
	Jurisdiction,
	JurisdictionSelector,
	MessageType,
	MultiDepositLimitConfigService,
	RESPONSIBLE_GAMING_ROUTE,
	ResponsibleGamingConfigService,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { CanComponentDeactivate, RouteAction, RouteLinkService, RouterState, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable, of, Subject, Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";

import {
	NetlossActiveLimit,
	NetLossLimit,
	NetlossLimitConfig,
	NetlossLimitContent,
	NetlossLimitLimitSubmission,
	NetlossLimitPeriod,
} from "../../netloss-limit/netloss-limit.model";
import { NetlossLimitService } from "../../netloss-limit/netloss-limit.service";
import { TimeOffConfig, TimeOffContent, TimeoutTranslation, TimoutPeriod } from "../../time-off/time-off.model";
import { TimeOffService } from "../../time-off/time-off.service";
import {
	balanceLimitLazyItem,
	bettingLimitLazyItem,
	depositLimitCalculatorLazyItem,
	depositLimitLazyItem,
	indefiniteSelfExclusionLazyItem,
	loginLimitLazyItem,
	netlossLimitLazyItem,
	bettingLimitV2LazyItem,
	selfExclusionLazyItem,
	timeOffLazyItem,
	zhhBettingLimitLazyItem,
} from "../model";
import { ResponsibleGamingFormService } from "../responsible-gaming-form.service";
import {
	RESPONSIBLE_GAMING_UNSAVED_CHANGES_ALERT_ID,
	ROUTE_ACCORDION_KEY_DICTIONARY,
} from "../responsible-gaming.consts";

@Component({
	selector: "obg-m-responsible-gaming-tabs-container",
	templateUrl: "./responsible-gaming-tabs.container.html",
	styleUrls: ["./responsible-gaming-tabs.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class ResponsibleGamingTabsContainer implements OnInit, OnDestroy, CanComponentDeactivate {
	@HostBinding("class.obg-m-responsible-gaming") hostClass = true;

	@ViewChild("tabs", { static: true }) tabs!: MatTabGroup;

	// TODO: once migrate to stencil v3 use WC type instead of any
	@ViewChild("rgNetlossLimitElement") rgNetlossLimitElement: any;
	@ViewChild("rgBettingLimitElement") rgBettingLimitElement: any;

	selectedIndex = -1;
	isMultiDepositLimitEnabled = false;
	isMultiSessionLimitEnabled = false;
	isBalanceLimitEnabled = false;
	isBettingLimitEnabled = false;
	isBettingLimitV2Enabled = false;
	isDepositLimitEnabled = false;
	isDepositLimitCalculatorEnabled = false;
	isNetlossLimitEnabled = false;
	isNetlossLimitWebComponentEnabled = this.responsibleGamingConfig.getNetlossLimitWebComponentEnabled();
	isBettingLimitWebComponentEnabled = this.responsibleGamingConfig.getBettingLimitWebComponentEnabled();
	isSelfExclusionEnabled = false;
	isIndefiniteSeTabEnabled = false;
	isTimeOffEnabled = false;
	isLoginLimitEnabled = false;
	isLogoutCooldownEnabled = false;
	isTimeOffWebComponentEnabled = this.responsibleGamingConfig.getTimeOffWebComponentEnabled();
	responsibleGamingRoutes: string[] = [];
	jurisdiction: Jurisdiction | undefined;
	isAuthenticated$: Observable<boolean> | undefined;
	isDirty = false;
	isJurisdictionAams: boolean | undefined;
	isArgentineJurisdiction = false;

	bettingLimitModule = bettingLimitLazyItem;
	depositLimitModule = depositLimitLazyItem;
	depositLimitCalculatorModule = depositLimitCalculatorLazyItem;
	netlossLimitModule = netlossLimitLazyItem;
	selfExclusionModule = selfExclusionLazyItem;
	indefiniteSelfExclusionModule = indefiniteSelfExclusionLazyItem;
	balanceLimitModule = balanceLimitLazyItem;
	timeOffModule = timeOffLazyItem;
	loginLimitModule = loginLimitLazyItem;

	isDepositLimitCalculatorEnabled$: Observable<boolean | undefined> =
		this.responsibleGamingConfigService.getDepositLimitCalculatorEnabled$();
	netlossLimitComponentConfig: NetlossLimitConfig | undefined;
	netlossLimitContent$: Observable<NetlossLimitContent> | undefined;
	netLossLimitActive$: Observable<NetlossActiveLimit | undefined> | undefined;
	netLossLimitQueued$: Observable<NetLossLimit | undefined> | undefined;
	netLossLimitPeriods$: Observable<NetlossLimitPeriod[] | undefined> | undefined;

	bettingLimitComponentConfig: BettingLimitConfig | undefined;
	bettingLimitActive$: Observable<BettingActiveLimit | undefined> | undefined;
	bettingLimitContent$: Observable<BettingLimitContent> | undefined;
	bettingLimitPeriods$: Observable<BettingLimitPeriod[] | undefined> | undefined;
	bettingLimitTypesContent$: Observable<BettingLimitPeriod[] | undefined> | undefined;
	bettingLimitForm: BettingLimitForm = this.bettingLimitService.getInitialForm();
	bettingQueuedLimit$: Observable<BettingLimit | undefined> | undefined;

	timOffContent$: Observable<TimeOffContent> | undefined;
	timeOffComponentConfig$: Observable<TimeOffConfig> | undefined;
	timeOffPeriods$: Observable<string[]> | undefined;
	timeOffTranslations$: Observable<TimeoutTranslation[]> | undefined;

	private data$$: Subscription | undefined;
	private loginDialog$$: Subscription | undefined;
	private routeDeactivate$ = new Subject<boolean>();
	private formState$$: Subscription | undefined;

	constructor(
		private store: Store<RouterState>,
		private authService: AuthService,
		private multiSessionLimitConfig: MultiSessionLimitConfigService,
		private responsibleGamingConfig: ResponsibleGamingConfigService,
		private currencyAction: CurrencyAction,
		private changeDetector: ChangeDetectorRef,
		private responsibleGamingConfigService: ResponsibleGamingUiConfigService,
		private multiDepositLimitConfigService: MultiDepositLimitConfigService,
		private netlossLimitService: NetlossLimitService,
		private timeOffService: TimeOffService,
		private responsibleGamingFormService: ResponsibleGamingFormService,
		private alertService: AbstractTranslatableAlertService,
		private bettingLimitService: BettingLimitService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private routeLinkService: RouteLinkService
	) {}

	ngOnInit() {
		this.initConfigValues();

		if (this.isNetlossLimitWebComponentEnabled) {
			this.getMfeNetlossLimitData();
		}

		if (this.isBettingLimitWebComponentEnabled) {
			this.getMfeBettingLimitData();
		}

		if (this.isTimeOffWebComponentEnabled) {
			this.getMfeTimeOffData();
		}

		// todo: OBGD-2171 implement a better, global solution
		this.tabs._handleClick = this.interceptTabChange.bind(this);
		this.store.dispatch(this.currencyAction.getAll());

		this.isAuthenticated$ = this.store.select(AuthSelector.isAuthenticated);

		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => {
				this.jurisdiction = jurisdiction;
				this.isArgentineJurisdiction =
					this.jurisdiction === Jurisdiction.Lotba ||
					this.jurisdiction === Jurisdiction.Iplyc ||
					this.jurisdiction === Jurisdiction.LPCSE;
				this.isJurisdictionAams = this.jurisdiction === Jurisdiction.Aams;
				if (this.jurisdiction === Jurisdiction.Zhh) {
					this.bettingLimitModule = zhhBettingLimitLazyItem;
				}
			})
		);

		const isDepositLimitEnabled$ = this.responsibleGamingConfigService.getDepositLimitEnabled$().pipe(
			ignoreNil(),
			tap((depositLimitEnabled) => (this.isDepositLimitEnabled = depositLimitEnabled))
		);

		const isDepositLimitCalculatorEnabled$ = this.responsibleGamingConfigService
			.getDepositLimitCalculatorEnabled$()
			.pipe(
				ignoreNil(),
				tap(
					(depositLimitCalculatorEnabled) =>
						(this.isDepositLimitCalculatorEnabled = depositLimitCalculatorEnabled)
				)
			);

		const balanceLimitEnabled$ = this.responsibleGamingConfigService.getBalanceLimitEnabled$().pipe(
			ignoreNil(),
			tap((balanceLimitEnabled) => (this.isBalanceLimitEnabled = balanceLimitEnabled))
		);

		const bettingLimitEnabled$ = this.responsibleGamingConfigService.getBettingLimitEnabled$().pipe(
			ignoreNil(),
			tap((bettingLimitEnabled) => (this.isBettingLimitEnabled = bettingLimitEnabled))
		);

		const bettingLimitV2Enabled$ = this.responsibleGamingConfigService.getBettingLimitV2Enabled$().pipe(
			ignoreNil(),
			tap((bettingLimitV2Enabled) => {
				this.isBettingLimitV2Enabled = bettingLimitV2Enabled;
				if (bettingLimitV2Enabled) {
					this.bettingLimitModule = bettingLimitV2LazyItem;
				}
			})
		);

		const netlossLimitEnabled$ = this.responsibleGamingConfigService.getNetlossLimitEnabled$().pipe(
			ignoreNil(),
			tap((netlossLimitEnabled) => (this.isNetlossLimitEnabled = netlossLimitEnabled))
		);

		const selfExclusionEnabled$ = this.responsibleGamingConfigService.getSelfExclusionEnabled$().pipe(
			ignoreNil(),
			tap((selfExclusionEnabled) => (this.isSelfExclusionEnabled = selfExclusionEnabled))
		);

		const indefiniteSelfExclusionEnabled$ = this.responsibleGamingConfigService
			.getIndefiniteSelfExclusionTabEnabled$()
			.pipe(
				ignoreNil(),
				tap((indefiniteTabEnabled) => (this.isIndefiniteSeTabEnabled = indefiniteTabEnabled))
			);

		const loginLimitEnabled$ = this.responsibleGamingConfigService.getLoginLimitEnabled$().pipe(
			ignoreNil(),
			tap((loginLimitEnabled) => (this.isLoginLimitEnabled = loginLimitEnabled))
		);

		const logoutCooldownEnabled$ = this.responsibleGamingConfigService.getLogoutCooldownEnabled$().pipe(
			ignoreNil(),
			tap((logoutCooldownEnabled) => (this.isLogoutCooldownEnabled = logoutCooldownEnabled))
		);

		const currentTab$ = combineLatest([
			jurisdiction$,
			isDepositLimitEnabled$,
			isDepositLimitCalculatorEnabled$,
			balanceLimitEnabled$,
			bettingLimitEnabled$,
			bettingLimitV2Enabled$,
			netlossLimitEnabled$,
			selfExclusionEnabled$,
			indefiniteSelfExclusionEnabled$,
			loginLimitEnabled$,
			logoutCooldownEnabled$,
		]).pipe(
			tap(() => this.initConfigValues()),
			switchMap(() => this.store.pipe(RouteSelector.getName$)),
			filter((name) => name.includes(RESPONSIBLE_GAMING_ROUTE.root)),
			tap((name) => {
				name === RESPONSIBLE_GAMING_ROUTE.root || !this.responsibleGamingRoutes.includes(name)
					? this.selectTab(0)
					: (this.selectedIndex = this.responsibleGamingRoutes.indexOf(name));
			})
		);

		this.data$$ = currentTab$.subscribe(() => this.changeDetector.markForCheck());

		this.formState$$ = this.responsibleGamingFormService
			.getIsFormDirty$()
			.subscribe((dirty) => (this.isDirty = dirty));
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.formState$$?.unsubscribe();

		if (this.loginDialog$$) {
			this.loginDialog$$.unsubscribe();
		}
	}

	interceptTabChange(tab: MatTab, tabHeader: MatTabHeader, id: number) {
		const route = this.responsibleGamingRoutes[id];
		this.responsibleGamingFormService.trackLinkClickGtmAction(ROUTE_ACCORDION_KEY_DICTIONARY[route]);
		if (this.isDirty && this.selectedIndex !== id) {
			this.openUnsavedChangesDialog(route);
			return;
		}

		if (!this.authService.isRouteSecure(route)) {
			this.selectTab(id);
			return MatTabGroup.prototype._handleClick.apply(this.tabs, arguments);
		}

		if (this.loginDialog$$) {
			this.loginDialog$$.unsubscribe();
		}
		this.loginDialog$$ = this.store
			.pipe(
				AuthSelector.isAuthenticated$,
				tap((isAuthenticated) => {
					if (!isAuthenticated) {
						this.store.dispatch(
							RouteAction.navigate(CORE_ROUTE.auth.login, undefined, {
								queryParams: { returnUrl: this.routeLinkService.getUrl({ route }) },
							})
						);
						return;
					}

					this.selectTab(id);
					return MatTabGroup.prototype._handleClick.apply(this.tabs, [tab, tabHeader, id]);
				}),
				filter((isAuthenticated) => !!isAuthenticated),
				take(1)
			)
			.subscribe();
	}

	selectTab(tabIndex: number) {
		if (tabIndex === this.selectedIndex) {
			return;
		}
		const route = this.responsibleGamingRoutes[tabIndex];
		this.store.dispatch(RouteAction.navigate(route, undefined, { replaceUrl: true }));
	}

	removeNetlossLimitRequest() {
		this.netlossLimitService.removeNetlossLimit(this.disableNetLossLimitEditLayout);
		this.changeDetector.markForCheck();
	}

	removeBettingLimitRequest() {
		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: "BETTING_LIMIT_REMOVE_ALERT_ID",
				testId: "betting-limit-deletion-confirmation",
				title: "betting-limit.confirmation-title",
				message: "betting-limit.deletion-confirmation-caption",
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							const limitType = this.bettingLimitForm.limitType;
							switch (limitType) {
								case BettingLimitType.BetSizeLimit:
									this.store.dispatch(BettingLimitAction.deleteBetSizeLimit());
									break;
								case BettingLimitType.WageringLimit:
									this.store.dispatch(BettingLimitAction.deleteWageringLimit());
									break;
								case BettingLimitType.TimedWageringLimit:
									this.store.dispatch(BettingLimitAction.deleteTimedWageringLimit());
									break;
								default:
							}

							this.resetBettingLimitForm();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	netlossLimitSubmission(event: CustomEvent<NetlossLimitLimitSubmission>) {
		this.netlossLimitService.submitNetlossLimit(
			Math.floor(event.detail?.amount),
			event.detail?.period,
			this.disableNetLossLimitEditLayout
		);
		this.changeDetector.markForCheck();
	}

	bettingLimitSubmission(event: CustomEvent<BettingLimitSubmission>) {
		this.bettingLimitForm.limit.value = event.detail.amount;

		switch (event.detail.period) {
			case LimitPeriod.Daily:
			case LimitPeriod.Weekly:
			case LimitPeriod.Monthly:
				this.bettingLimitForm.period = event.detail.period as LimitPeriod;
				this.bettingLimitForm.limitType = BettingLimitType.TimedWageringLimit;
				break;
			case BettingLimitType.BetCountLimit:
			case BettingLimitType.BetSizeLimit:
			case BettingLimitType.NetlossLimit:
			case BettingLimitType.TimedWageringLimit:
			case BettingLimitType.WageringLimit:
				this.bettingLimitForm.limitType = event.detail.period as BettingLimitType;
				break;
		}

		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: "BETTING_LIMIT_SUBMIT_ALERT_ID",
				testId: "betting-limit-submit-confirmation",
				title: `${TRANSLATION_PREFIX}.confirmation-title`,
				message: `${TRANSLATION_PREFIX}.confirmation-caption`,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							const limitType = this.bettingLimitForm.limitType;
							if (limitType === BettingLimitType.BetSizeLimit) {
								this.store.dispatch(
									BettingLimitAction.updateBetSizeLimit({
										limitValue: this.bettingLimitForm.limit.value!,
									})
								);
							} else if (limitType === BettingLimitType.WageringLimit) {
								this.store.dispatch(
									BettingLimitAction.updateWageringLimit({
										amount: this.bettingLimitForm.limit.value!,
									})
								);
							} else if (limitType === BettingLimitType.TimedWageringLimit) {
								this.store.dispatch(
									BettingLimitAction.updateTimedWageringLimit({
										period:
											LIMIT_PERIODS.get(this.bettingLimitForm.period as LimitPeriod) ||
											LimitPeriod.Daily,
										limitValue: this.bettingLimitForm.limit.value!,
									})
								);
							}

							this.snackBarService
								.openSnackBar$({
									prefixJurisdiction: true,
									message: `${TRANSLATION_PREFIX}.update-success`,
									action: "general.close",
									duration: 4000,
									type: MessageType.Success,
									id: "betting-limit-update-success",
								})
								.pipe(take(1), tap(this.disableBettingLimitEditLayout))
								// eslint-disable-next-line rxjs/no-ignored-subscription
								.subscribe();

							this.resetBettingLimitForm();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	take24HourBreak() {
		this.timeOffService.show24HourBreakAlert();
		this.changeDetector.markForCheck();
	}

	timeOffSubmit(event: CustomEvent<TimoutPeriod>) {
		this.timeOffService.showTimeOffAlert(event.detail?.key);
		this.changeDetector.markForCheck();
	}

	markUnsavedChanges(event: CustomEvent<boolean>) {
		this.isDirty = event.detail;
	}

	canDeactivate(_currentRoute: ActivatedRouteSnapshot, _currentState: RouterStateSnapshot) {
		if (!this.isDirty) {
			return true;
		}

		return this.store.select(AuthSelector.isAuthenticated).pipe(
			take(1),
			switchMap((isAuth) => {
				if (!isAuth) {
					return of(true);
				}

				this.openUnsavedChangesDialog();
				return this.routeDeactivate$;
			})
		);
	}

	onBettingLimitType(selectedLimitType: BettingLimitType | LimitPeriod): void {
		if (Object.values(BettingLimitType).includes(selectedLimitType as BettingLimitType)) {
			this.bettingLimitContent$ = this.bettingLimitService.getBettingLimitContent$(
				selectedLimitType as BettingLimitType
			);
			this.bettingLimitForm.limitType = selectedLimitType as BettingLimitType;
		}
	}

	private getMfeNetlossLimitData() {
		this.netLossLimitActive$ = this.netlossLimitService.getActiveNetLossLimit$();
		this.netLossLimitQueued$ = this.netlossLimitService.getQueuedNetLossLimit$();
		this.netLossLimitPeriods$ = this.netlossLimitService.getNetLossLimitPeriods$();
		this.netlossLimitContent$ = this.netlossLimitService.getNetlossLimitContent$();
		this.netlossLimitComponentConfig = this.netlossLimitService.getNetlossLimitComponentConfig();
	}

	private getMfeTimeOffData() {
		this.timOffContent$ = this.timeOffService.getTimOffContent$();
		this.timeOffComponentConfig$ = this.timeOffService.getTimeOffComponentConfig$();
		this.timeOffPeriods$ = this.timeOffService.getTimeOffPeriods$();
		this.timeOffTranslations$ = this.timeOffService.getTimeOffTranslations$();
	}

	private getMfeBettingLimitData(): void {
		this.bettingLimitActive$ = this.bettingLimitService.getActiveBettingLimit$().pipe(
			tap((activeLimit) => {
				if (activeLimit?.data?.type) {
					this.bettingLimitForm.limitType = activeLimit?.data?.type;
				}
			})
		);
		this.bettingLimitComponentConfig = this.bettingLimitService.getBettingLimitComponentConfig();
		this.bettingLimitContent$ = this.bettingLimitService.getBettingLimitContent$();
		this.bettingLimitTypesContent$ = this.bettingLimitService.getbettingLimitType$();
		this.bettingQueuedLimit$ = this.bettingLimitService.getbettingQueuedLimit$();
		this.bettingLimitPeriods$ = this.bettingLimitService.getBettingLimitPeriods$();
	}

	private resetBettingLimitForm() {
		this.bettingLimitForm = this.bettingLimitService.getInitialForm();
	}

	private openUnsavedChangesDialog(route?: string) {
		this.alertService.open(
			{
				id: RESPONSIBLE_GAMING_UNSAVED_CHANGES_ALERT_ID,
				testId: RESPONSIBLE_GAMING_UNSAVED_CHANGES_ALERT_ID,
				title: "responsible-gaming.navigation-warning.title",
				message: "responsible-gaming.navigation-warning.message",
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
						handler: () =>
							this.routeDeactivate$.next(!!route && this.responsibleGamingRoutes.indexOf(route) !== -1),
					},
					{
						text: "general.confirm",
						handler: () => {
							this.routeDeactivate$.next(true);
							this.isDirty = false;
							if (route && this.responsibleGamingRoutes.indexOf(route) !== -1) {
								this.selectTab(this.responsibleGamingRoutes.indexOf(route));
							}
						},
					},
				],
				hideClose: true,
				dialogConfig: { disableClose: true },
				mustBeInteracted: true,
			},
			[TranslationGroup.Common]
		);
	}

	private initConfigValues() {
		this.isMultiDepositLimitEnabled = this.multiDepositLimitConfigService.getMultiDepositLimitEnabled();
		this.isMultiSessionLimitEnabled = this.multiSessionLimitConfig.getMultiSessionLimitEnabled();
		this.isTimeOffEnabled = this.responsibleGamingConfig.getTimeOffEnabled();
		this.isTimeOffWebComponentEnabled = this.responsibleGamingConfig.getTimeOffWebComponentEnabled();

		this.responsibleGamingRoutes = [
			CORE_ROUTE.responsibleGaming.information,
			...(this.isDepositLimitEnabled ? [CORE_ROUTE.responsibleGaming.depositLimit] : []),
			...(this.isDepositLimitCalculatorEnabled ? [CORE_ROUTE.responsibleGaming.depositLimitCalculator] : []),
			...(this.isMultiSessionLimitEnabled ? [CORE_ROUTE.responsibleGaming.sessionLimit] : []),
			...(this.isBalanceLimitEnabled ? [CORE_ROUTE.responsibleGaming.balanceLimit] : []),
			...(this.isBettingLimitEnabled || this.isBettingLimitV2Enabled
				? [CORE_ROUTE.responsibleGaming.bettingLimit]
				: []),
			...(this.isNetlossLimitEnabled ? [CORE_ROUTE.responsibleGaming.netlossLimit] : []),
			...(this.isLogoutCooldownEnabled ? [CORE_ROUTE.responsibleGaming.logoutCooldown] : []),
			...(this.isTimeOffEnabled ? [CORE_ROUTE.responsibleGaming.timeOff] : []),
			...(this.isLoginLimitEnabled ? [CORE_ROUTE.responsibleGaming.loginLimit] : []),
			...(this.isSelfExclusionEnabled ? [CORE_ROUTE.responsibleGaming.selfExclusion] : []),
			...(this.isIndefiniteSeTabEnabled ? [CORE_ROUTE.responsibleGaming.indefiniteSelfExclusion] : []),
			CORE_ROUTE.responsibleGaming.underageGambling,
			CORE_ROUTE.responsibleGaming.casinoGamesWins,
		];
	}

	private disableNetLossLimitEditLayout = () => {
		this.rgNetlossLimitElement?.nativeElement?.hideEdit();
	};

	private disableBettingLimitEditLayout = () => {
		this.rgBettingLimitElement?.nativeElement?.hideEdit();
	};
}
